import * as React from 'react';
import styled from 'styled-components';

import { Box } from '@origin-digital/style-guide/dist/cjs/components/Box';

import { IBannerGraphic } from '../../models/content.interfaces';
import { DangerousHtml } from '../DangerousHtml';
import { CallToActions } from '../CallToAction/CallToAction';
import { Heading2, Heading3 } from '../Text/Text';
import { Byline } from '../Text';
import { BREAK_POINTS, COLOURS, NAV } from '../../consts/style';
import { GoodEnergy } from '../GoodEnergy';
import { DiscountItems } from '../Discount/Discount';
import { BannerPostcodePrompt } from '../BannerPostcodePrompt';

const Container = styled.div`
  position: relative;
  display: block;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3e %3cpath fill='white' fill-rule='evenodd' d='M0 80V0h80v80H0zm56-40a16 16 0 1 0-32 0 16 16 0 0 0 32 0zm-7.7 0a8.3 8.3 0 0 1-8.3 8.3 8.3 8.3 0 0 1-8.3-8.3 8.3 8.3 0 0 1 8.3-8.3 8.3 8.3 0 0 1 8.3 8.3z'/%3e %3c/svg%3e");
  background-size: 40px 40px;
  background-position: 50% 50%;

  @media (min-width: ${BREAK_POINTS.MIN.SM}) {
    min-height: 268px;
  }
`;

const Content = styled.div`
  margin-left: auto;
  margin-right: auto;

  @media (min-width: ${BREAK_POINTS.MIN.SM}) {
    width: 720px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    width: 940px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    width: 1140px;
  }
`;

const Spacing = styled.div`
  margin-left: ${(p: { centerAlign?: boolean }) => (p.centerAlign ? 'auto' : '0')};
  margin-right: ${(p: { centerAlign?: boolean }) => (p.centerAlign ? 'auto' : '0')};
  max-width: ${NAV.UPPER_WIDTH}
  text-align: ${(p: { centerAlign?: boolean }) => (p.centerAlign ? 'center' : 'left')};
  padding: 24px 16px;
`;

const HeadingWithImg = styled(Heading2.withComponent('h1'))`
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
`;

const GoodEnergyCentered = styled(GoodEnergy)`
  display: block;
  margin: 0 auto;
`;

const DiscountHeading = styled(Heading3.withComponent('h1'))`
  font-size: 18px;
  font-weight: 600;

  @media (min-width: 992px) {
    font-size: 20px;
  }
`;

const StandardHeading = Heading2.withComponent('h1');

const Title = ({ bannerStyle, centerAlign, headline }) => (
  <>
    {bannerStyle === 'good-energy' && (
      <>
        {centerAlign ? <GoodEnergyCentered /> : <GoodEnergy />}
        <HeadingWithImg>{headline}</HeadingWithImg>
      </>
    )}
    {bannerStyle === 'discount' && <DiscountHeading>{headline}</DiscountHeading>}
    {bannerStyle === 'standard' && <StandardHeading>{headline}</StandardHeading>}
  </>
);

const PricingInfo = styled.div`
  font-size: 14px;
  color: ${COLOURS.PRIMARY.GREY};
  padding-top: 16px;
  a {
    color: ${COLOURS.PRIMARY.GREY};
  }

  @media (max-width: ${BREAK_POINTS.MAX.XS}) {
    padding-top: 0px;
    padding-bottom: 16px;
  }
`;

const PlanDisclaimer = styled(PricingInfo)`
  font-size: 12px;
  line-height: 1.67;
  padding: 0px;
`;

const FlexRow = styled.div`
  @media (min-width: ${BREAK_POINTS.MIN.XS}) {
    display: flex;
    flex-direction: row;
  }
`;

export const BannerGraphic = ({
  headline,
  bylineHtml,
  bannerStyle = 'standard',
  ctas,
  discounts,
  showDisclaimer = false,
  pricingText = '',
  disclaimerText = '',
  postcodeEntry = false,
}: IBannerGraphic) => {
  const backgroundColor = 'rgba(255,180,50,.16)';
  const showDiscounts = bannerStyle === 'discount';
  const discountsAvailable = showDiscounts && discounts && discounts.length > 0;
  const centerAlign = !discountsAvailable && (!ctas || ctas.length <= 1);

  return (
    <div style={{ backgroundColor: '#fff' }} data-comment="BannerGraphic">
      {postcodeEntry ? (
        <BannerPostcodePrompt />
      ) : (
        <Container style={{ backgroundColor }}>
          <Content>
            <Spacing centerAlign={centerAlign}>
              <Title bannerStyle={bannerStyle} centerAlign={centerAlign} headline={headline} />

              {bannerStyle === 'discount' ? (
                <DiscountItems showDisclaimer={showDisclaimer} discounts={discounts} />
              ) : (
                <Byline>
                  <DangerousHtml html={bylineHtml} />
                </Byline>
              )}
              <FlexRow>
                <Box marginRight={8} marginBottom={2}>
                  <CallToActions
                    centerAlign={centerAlign}
                    ctas={ctas}
                    trackingCode="bannerButton"
                  />
                </Box>
                {pricingText ? (
                  <PricingInfo dangerouslySetInnerHTML={{ __html: pricingText }} />
                ) : null}
              </FlexRow>
              {disclaimerText ? (
                <PlanDisclaimer dangerouslySetInnerHTML={{ __html: disclaimerText }} />
              ) : null}
            </Spacing>
          </Content>
        </Container>
      )}
    </div>
  );
};
